import { useEffect, useRef, useCallback, useState } from "react"
import { useClientAuth } from "hooks/useAxios"

/**
 * Provides a short-lived session token to the ref-db iframe via postMessage.
 *
 * Protocol:
 *   1. iframe → parent:  { type: "IFRAME_READY" }
 *   2. parent → iframe:  { type: "SESSION_TOKEN", token: "..." }
 *
 * @param {string} refDbOrigin
 * @param {React.RefObject} iframeRef
 */
const useRefDbSessionToken = (refDbOrigin, iframeRef) => {
  const client = useClientAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [error, setError] = useState(null)
  const tokenRef = useRef(null)
  const tokenSent = useRef(false)
  const fetchedRef = useRef(false)

  const sendTokenToIframe = useCallback(
    (token) => {
      const iframe = iframeRef.current
      if (!iframe?.contentWindow) return

      iframe.contentWindow.postMessage(
        { type: "SESSION_TOKEN", token },
        refDbOrigin
      )
      tokenSent.current = true
    },
    [refDbOrigin, iframeRef]
  )

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== refDbOrigin) return
      if (event.data?.type !== "IFRAME_READY") return
      if (tokenRef.current) sendTokenToIframe(tokenRef.current)
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [refDbOrigin, sendTokenToIframe])

  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true

    let cancelled = false

    const fetchToken = async () => {
      try {
        setIsLoading(true)
        setIsError(false)
        const response = await client.get(
          "/api/reference_database/session_token"
        )
        if (cancelled) return

        const { token } = response.data
        tokenRef.current = token

        if (!tokenSent.current && iframeRef.current?.contentWindow) {
          sendTokenToIframe(token)
        }
      } catch (err) {
        if (cancelled) return
        setIsError(true)
        setError(err?.response?.data?.error || err.message)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchToken()

    return () => {
      cancelled = true
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { isLoading, isError, error }
}

export default useRefDbSessionToken
